<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="inertia-link"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: meta.links.show,
                    },
                    {
                        name: $tc('messages.rubrics', 1),
                        href: null,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <NavLinks :links="meta.navLinks" />
        </div>

        <div class="mt-3 flex justify-end space-x-1">
            <AppButton element="a" :href="meta.links.print">
                <svg viewBox="0 0 20 20" fill="currentColor" class="-ml-0.5 mr-2 w-4 h-4">
                    <path
                        fill-rule="evenodd"
                        d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
                {{ $t('messages.template') }} - {{ $t('messages.print') }}
            </AppButton>
            <AppButton element="a" :href="meta.links.print_students">
                <svg viewBox="0 0 20 20" fill="currentColor" class="-ml-0.5 mr-2 w-4 h-4">
                    <path
                        fill-rule="evenodd"
                        d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
                {{ $t('messages.template') }} - {{ $tc('messages.students', 2) }}
            </AppButton>
        </div>

        <AppWell class="mt-3">
            <div class="md:grid md:grid-cols-4 xl:grid-cols-5 md:gap-6">
                <AppCard v-for="student in students" :key="student.id" class="mt-1 sm:mt-0">
                    <div class="flex">
                        <InertiaLink :href="student.links.show">
                            <div class="flex space-x-2 items-center">
                                <img
                                    :src="student.avatar_or_default"
                                    :alt="student.full_name"
                                    class="rounded-full h-10"
                                    :class="{
                                        'opacity-50': student.is_absent && meta.settings.distinguish_absent_students,
                                    }"
                                />
                                <div class="text-sm">
                                    {{ student.index }}.
                                    <span class="text-indigo-600">{{ student.full_name }}</span>
                                </div>
                            </div>
                        </InertiaLink>
                    </div>
                    <div class="flex justify-between mt-1 items-center">
                        <div>
                            <BadgePill
                                :color="student.score.color"
                                :title="student.score.score"
                                v-if="student.has_score"
                            >
                                {{ student.score.letter || $t('messages.absent') }}
                            </BadgePill>
                        </div>

                        <div class="flex justify-end gap-x-1 items-center">
                            <InertiaSquareDeleteButton
                                v-if="student.links.response_delete"
                                :href="student.links.response_delete"
                                :confirm-text="$t('messages.are_you_sure?')"
                            />
                            <InertiaLink
                                :href="student.links.edit"
                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    stroke-width="2"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M8 20l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4h4z"></path>
                                    <path d="M13.5 6.5l4 4"></path>
                                    <path d="M16 18h4m-2 -2v4"></path>
                                </svg>
                            </InertiaLink>
                        </div>
                    </div>
                </AppCard>
            </div>
        </AppWell>

        <ActivityGradeResponsesGraphs
            :bar-chart-options="graphs.barChartOptions"
            :line-chart-options="graphs.lineChartOptions"
            :values="graphs.decimalScores"
            :ranges="graphs.ranges"
        />
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import GradeTranslator from '@/classes/GradeTranslator.js';
import BadgePill from '@/components/BadgePill.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import ActivityGradeResponsesGraphs from '@/components/pageSpecific/ActivityGradeResponsesGraphs.vue';
import AppButton from '@/components/AppButton.vue';
import NavLinks from '@/components/NavLinks.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';

export default {
    components: {
        InertiaSquareDeleteButton,
        PanelPage,
        AppButton,
        BadgePill,
        AppCard,
        AppWell,
        ActivityGradeResponsesGraphs,
        NavLinks,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'students', 'activity', 'group', 'graphs'],
    data() {
        return {
            gradeTranslator: new GradeTranslator(this.ranges),
        };
    },
    methods: {},
};
</script>
